import type { StatusTone } from '../../design-system';
import type { AbsenceTimeOffRequest, AbsenceTimeOffRow, AbsenceTimeOffType } from './model';
import { absenceTypeWithDayPart } from './absenceDayPart';
import { entryStatus, requestStatus } from './statusPresentation';

export type AbsenceCalendarItem = {
  id: string;
  kind: 'request' | 'entry';
  typeId: string;
  displayColor?: AbsenceTimeOffType['displayColor'];
  title: string;
  startDate: string;
  endDate: string;
  quantity: string;
  unitOfTime: string;
  statusLabel: string;
  tone: StatusTone;
  closed: boolean;
  request?: AbsenceTimeOffRequest;
  entry?: AbsenceTimeOffRow;
};

export function buildAbsenceCalendarItems(requests: readonly AbsenceTimeOffRequest[], entries: readonly AbsenceTimeOffRow[], types: readonly Pick<AbsenceTimeOffType, 'id' | 'displayColor'>[] = []): AbsenceCalendarItem[] {
  const typeColors = new Map(types.map((type) => [type.id, type.displayColor]));
  const linkedEntries = entries.flatMap((entry) => entry.source ? [entry.source] : []);
  const linkedRequestIds = new Set(linkedEntries.map((entry) => entry.sourceRequest?.id).filter(Boolean));
  const confirmedRequestSignatures = new Set(linkedEntries.filter((entry) => entry.status === 'CONFIRMED').map((entry) => `${entry.timeOffType.id}|${entry.startDate}|${entry.endDate}|${entry.quantity}|${entry.dayPart || 'FULL_DAY'}`));
  const requestItems: AbsenceCalendarItem[] = requests
    .filter((request) => !linkedRequestIds.has(request.id) && !(request.status === 'APPROVED' && confirmedRequestSignatures.has(`${request.timeOffType.id}|${request.startDate}|${request.endDate}|${request.quantity}|${request.dayPart || 'FULL_DAY'}`)))
    .map((request) => {
      const presentation = requestStatus(request.status, request.cancellationStatus);
      return { id: request.id, kind: 'request', typeId: request.timeOffType.id, title: absenceTypeWithDayPart(request.timeOffType.descriptor || request.descriptor, request.dayPart), startDate: request.startDate, endDate: request.endDate, quantity: request.quantity, unitOfTime: request.unitOfTime, statusLabel: presentation.label, tone: presentation.tone, closed: presentation.closed, request };
    });
  const entryItems: AbsenceCalendarItem[] = entries.filter((entry) => entry.source).map((entry) => {
    const source = entry.source!;
    const request = requests.find((item) => item.id === source.sourceRequest?.id);
    const presentation = request?.cancellationStatus === 'SUBMITTED' && source.status === 'CONFIRMED' ? requestStatus(request.status, request.cancellationStatus) : entryStatus(source.status);
    return { id: entry.id, kind: 'entry', typeId: source.timeOffType.id, title: absenceTypeWithDayPart(entry.type, source.dayPart), startDate: source.startDate, endDate: source.endDate, quantity: source.quantity, unitOfTime: source.unitOfTime, statusLabel: presentation.label, tone: presentation.tone, closed: presentation.closed, request, entry };
  });
  return [...requestItems, ...entryItems].map((item) => ({ ...item, displayColor: typeColors.get(item.typeId) ?? 'BLUE' }));
}

function utcDate(year: number, month: number, day: number): Date {
  const result = new Date(0);
  result.setUTCFullYear(year, month, day);
  return result;
}

function parseDate(date: string): Date | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return undefined;
  const [year, month, day] = date.split('-').map(Number);
  if (year < 1 || month < 1 || month > 12 || day < 1 || day > 31) return undefined;
  const parsed = utcDate(year, month - 1, day);
  return parsed.getUTCFullYear() === year && parsed.getUTCMonth() === month - 1 && parsed.getUTCDate() === day ? parsed : undefined;
}

// 배열과 weekday 모두 일요일 0, 토요일 6인 Date 계약을 따른다.
export function calendarMonthDays(year: number, month: number): Array<{ date: string; day: number; weekday: number } | null> {
  const cells: Array<{ date: string; day: number; weekday: number } | null> = Array.from({ length: 42 }, () => null);
  if (!Number.isInteger(year) || year < 1 || year > 9999 || !Number.isInteger(month) || month < 0 || month > 11) return cells;
  const startOffset = utcDate(year, month, 1).getUTCDay();
  const daysInMonth = utcDate(year, month + 1, 0).getUTCDate();
  for (let day = 1; day <= daysInMonth; day += 1) {
    cells[startOffset + day - 1] = {
      date: `${String(year).padStart(4, '0')}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      day,
      weekday: utcDate(year, month, day).getUTCDay(),
    };
  }
  return cells;
}

// 신청 기간을 달력일로 표시하며 근무일이나 사용 수량을 다시 계산하지 않는다.
export function calendarItemsOnDate(items: readonly AbsenceCalendarItem[], date: string): AbsenceCalendarItem[] {
  if (!parseDate(date)) return [];
  return items.filter((item) => parseDate(item.startDate) && parseDate(item.endDate) && item.startDate <= date && date <= item.endDate);
}

export function formatAbsenceDate(date: string): string {
  const parsed = parseDate(date);
  return parsed ? new Intl.DateTimeFormat('ko-KR', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' }).format(parsed) : '-';
}

export function localToday(): string {
  const today = new Date();
  return `${String(today.getFullYear()).padStart(4, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
}
